import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Modal from '@mui/joy/Modal';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import IconButton from '@mui/joy/IconButton';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardActions from '@mui/joy/CardActions';

import { RootStoreContext } from '../stores/root_store';
import { CollabProps } from '../stores/collab_store';
import { CollaboratorsProps } from '../stores/collaborators_store';

import CollabInviteList from './CollabInviteList';
import IdentityAvatar from './IdentityAvatar';
import CollabClockTower from './CollabClockTower';
import CollabWorkSpaceSidebarCallLog from './CollabWorkSpaceSidebarCallLog';
import CollabWorkSpaceSidebarFiles from './CollabWorkSpaceSidebarFiles';
import SidebarIdentityInfo from 'components/SidebarIdentityInfo';
import LockedFeature from 'components/LockedFeature';


import getIcon from 'utils/getIcon';

import RatingCardThumbs from "components/RatingCardThumbs";
import { Success, Error } from 'components/FancyToast';
import AssociatedTools from 'components/AssociatedTools';
import CallIncomingListByIncomingPhoneNumber from 'components/CallIncomingListByIncomingPhoneNumber';
import CollabWorkSpaceSidebarEmail from 'components/CollabWorkSpaceSidebarEmail';

import useUserIdentity from 'hooks/useUserIdentity';
import useCollaborators from 'hooks/useCollaborators';
import useRating from 'hooks/useRating';
import useClock from 'hooks/useClock';
import useCollabs from 'hooks/useCollabs';
import ClockTotalSidebar from 'components/ClockTotalSidebar';

function CollaboratorStatus({ collaborator }: { collaborator: CollaboratorsProps }) {
    const SnoozeIcon = getIcon("PendingActions");
    const RevokedIcon = getIcon("PersonOff");
    const LeftIcon = getIcon("DirectionsRun");
    const RemovedIcon = getIcon("PersonRemove");

    if (collaborator.status === "active") {
        return null;
    }

    function _icon(status: string) {
        if (status == "snoozed")
            return SnoozeIcon;
        else if (status == "left")
            return LeftIcon;
        else if (status == "revoked")
            return RevokedIcon;
        else if (status == "removed")
            return RemovedIcon;

        return RevokedIcon;
    }

    function _copy(status: string) {
        if (status == "snoozed")
            return "If you send a message, they'll get it and continue working."
        else if (status == "left")
            return "They won't return to this."
        else if (status == "revoked")
            return "They cannot return to this."
        else if (status == "removed")
            return "They can rejoin if you invite another Day One."

    }

    const Icon = _icon(collaborator.status);

    return (
        <Tooltip arrow title={`${_copy(collaborator.status)}`}>
            <Chip
                endDecorator={<Icon />}
                sx={{ textTransform: 'capitalize' }}
                size="sm">
                {collaborator.status}
            </Chip>
        </Tooltip>
    );
}

const CollaboratorRemoveModal = observer(({ setOpen, open, collaborator }: { setOpen: (k: boolean) => any, open: boolean, collaborator: CollaboratorsProps }) => {
    const [score, setScore] = useState(0);
    const { revoke, remove } = useCollaborators();
    const { rateItem } = useRating();
    const { currentIdentity } = useUserIdentity();


    function doRating() {
        if (score === 1 || score === -1) {
            return rateItem(collaborator.uuid,
                score,
                currentIdentity.key,
                collaborator.identity)
                .then(() => {
                    Success("Rated this!");
                }).
                catch((e) => {
                    console.error(e);
                    Error("Failed to rate this");
                });
        }
    }

    function doRemove() {
        console.log("in do remove");
        remove(collaborator.identity, collaborator.collab, currentIdentity.key)
            .then(() => {
                if (score === 1 || score === -1) {
                    doRating();
                }
                else {
                    Success("Removed collaborator");
                }
                setOpen(false);
            })
            .catch(() =>
                Error("Unable to remove collaborator")
            );
    }


    function doRevoke() {
        // console.log("in do remove");
        revoke(collaborator.identity, collaborator.collab, currentIdentity.key)
            .then(() => {
                if (score === 1 || score === -1) {
                    doRating();
                }
                else {
                    Success("Revoked collaborator");
                }
                setOpen(false);
            })
            .catch(() =>
                Error("Unable to revoke collaborator")
            );
    }

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
            <Card>

                <Typography level="title-md">
                    Time to remove a collaborator?
                </Typography>

                <Typography level="title-sm">
                    They will not be able to clock in on this collab.
                </Typography>

                <RatingCardThumbs
                    label={<Typography level="title-sm">
                        Rate this collaboration with <IdentityAvatar
                            size="chip"
                            interactive={false}
                            identityKey={collaborator.identity} />
                    </Typography>}
                    setScore={setScore}
                    score={score} />
                <CardActions>

                    <Tooltip title="They will NOT be able to join again.">
                        <Button
                            onClick={() => doRevoke()}
                            variant="outlined"
                            color="danger"
                        >Revoke this Collaborator</Button>
                    </Tooltip>
                    <Tooltip title="They will be allowed to join again later if you invite more collaborators.">
                        <Button
                            onClick={() => doRemove()}
                            variant="outlined"
                            color="success"
                        >Remove this Collaborator</Button>
                    </Tooltip>

                </CardActions>
            </Card>

        </Modal>
    );
});


function CollaboratorAction({ collaborator }: { collaborator: CollaboratorsProps }) {
    const [confirming, setConfirming] = useState(false);
    const [clicked, setClicked] = useState(false);

    if (collaborator.status === "left")
        return null;

    if (confirming) {
        return <CollaboratorRemoveModal
            open={confirming}
            setOpen={setConfirming}
            collaborator={collaborator} />;
    }
    if (clicked)
        return null;

    return (
        <Typography
            sx={{ textTransform: 'capitalize' }}
            level="body-xs">
            <Button
                size="sm"
                variant="outlined"
                color="danger"
                onClick={() => { setConfirming(!confirming); setClicked(true) }}
            >   Remove this Collaborator</Button>
        </Typography>
    );

}


const CollaboratorSidebarOptions = observer(({ collaborator }: { collaborator: CollaboratorsProps }) => {
    const { currentIdentity } = useUserIdentity();
    if (collaborator.identity && (collaborator.identity == currentIdentity?.key)) {
        return null;
    }
    return (
        <Stack spacing={1} p={1}>
            <CollaboratorStatus collaborator={collaborator} />
            <CollaboratorAction collaborator={collaborator} />
        </Stack>
    );
});

const CollaboratorSidebarItem = observer(({ collaborator, collab }: { collaborator: CollaboratorsProps, collab: CollabProps }) => {
    // const identityStore = React.useContext(RootStoreContext).identityStore;
    // const identity = identityStore.getIdentity(collaborator.identity);
    const { currentIdentity, isYou } = useUserIdentity();
    const [editing, setEditing] = useState(false);
    const EditIcon = getIcon("PersonRemove");
    const gone = ['left', 'revoked', 'removed'].includes(collaborator.status);
    const editable = isYou(collab.identity) && ((collaborator.identity !== currentIdentity?.key) && !gone);

    return (
        <ListItem>
            <ListItemContent>
                <Stack>
                    <Stack direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ '--Avatar-size': '22px' }}
                    >

                        <IdentityAvatar
                            interactive={true}
                            size="inline"
                            identityKey={collaborator.identity} />

                        {collaborator.status != "active" &&
                            <Typography level="body-xs">
                                <CollaboratorStatus collaborator={collaborator} />
                            </Typography>}
                        {(editable) &&
                            <Tooltip placement="bottom-start" title="Click to view collaborator actions" arrow>
                                <IconButton onClick={() => setEditing(!editing)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </Stack>

                    {editing &&
                        <CollaboratorSidebarOptions collaborator={collaborator} />}
                </Stack>

            </ListItemContent>



        </ListItem>
    );
});



export const CollabWorkSpaceSidebarFullPage = () => {
    const { collabKey = "" } = useParams<{ collabKey: string }>();
    const { currentCollab, fetchCollab, collabByKey, collabs } = useCollabs();
    const { currentIdentity } = useUserIdentity();
    const navigate = useNavigate();
    const ArrowBack = getIcon('ArrowBack');
    const [collab, setCurrentCollab] = useState(currentCollab);

    useEffect(() => {
        const c = collabByKey(collabKey);
        setCurrentCollab(c);
        document.title = (c?.title || "Collab") + " (sidebar)";;
    }, [collabKey, collabByKey, collabs.length]);

    useEffect(() => {
        if (currentIdentity?.key) {
            fetchCollab(currentIdentity.key, collabKey);
        }
    }, [collabKey, fetchCollab, currentIdentity.key]);

    if (!collab)
        return null;

    return (
        <Stack>
            <Stack
                m={2}
                justifyContent="flex-start"
                direction="row"
            >
                <Button
                    startDecorator={<ArrowBack />}
                    variant="plain"
                    onClick={() => navigate(`/collab/${collabKey}`)}
                >View Collab</Button>
            </Stack>
            <CollabWorkSpaceSidebar
                collab={collab}
            />
        </Stack>
    );
};



const CollabWorkSpaceSidebar = observer(({ collab }: { collab: CollabProps }) => {
    const rootStore = React.useContext(RootStoreContext);
    const { currentIdentity } = useUserIdentity();
    const { currentClock } = useClock();

    useEffect(() => {
        if (collab && collab.key) {
            rootStore.collaboratorsStore.fetchListForCollab(currentIdentity?.key, collab.key);
        }
    }, [rootStore.collaboratorsStore, collab, currentIdentity.key]);


    if (!collab || !collab.key) {
        return null;
    }

    const collaborators = rootStore.collaboratorsStore.getCollaborators(collab.key);

    return (
        <Stack m={1} spacing={1}>

            <SidebarIdentityInfo identityKey={collab.identity} />




            <LockedFeature
                outlined={false}
                isLocked={false}
                lockedIconName="Group"
               unlockedIconName="Group"
                copy="Collaborators">
                <Stack>

                    <List>
                        { collaborators.length > 1 &&
                            collaborators.map((c) =>
                                <CollaboratorSidebarItem
                                    key={c.key}
                                    collab={collab}
                                    collaborator={c} />)
                        }

                        <CollabInviteList collab={collab} />
                    </List>
            </Stack>
                </LockedFeature>


            <ClockTotalSidebar total_seconds={collab.clock_total_seconds} />

            {currentClock && currentClock.associated_uuid === collab.uuid &&
                <AssociatedTools
                    associated_type={"collab"}
                    associated_uuid={collab.uuid}
                    from_email={'collab-' + collab.uniq + '@email.fhands.com'}
                />
            }

            <LockedFeature
                lockedIconName="DnsOutlined"
                unlockedIconName="Dns"
                copy="Extra Details">



                <CollabWorkSpaceSidebarFiles type="collab" type_uuid={collab.uuid} />
            <CollabWorkSpaceSidebarEmail
                associated_type="collab"
                associated_uuid={collab.uuid} />

                <CollabWorkSpaceSidebarCallLog
                    associated_type="collab"
                    associated_uuid={collab.uuid} />

                <CallIncomingListByIncomingPhoneNumber
                    associated_type="collab"
                    associated_uuid={collab.uuid} />

                <CollabClockTower
                    associated_type="collab"
                    associated_uuid={collab.uuid} />
            </LockedFeature>

        </Stack>

    );
});



export default CollabWorkSpaceSidebar;
